import NiceModal from '@ebay/nice-modal-react';
import PropTypes from 'prop-types';

import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import SearchInput from 'components/field/SearchInput';
import Status from 'components/field/Status';
import SwitchInput from 'components/field/SwitchInput';
import { NonFormContainer } from 'components/form/FormLayout.styled';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import NoRecordsFound from 'components/global/NoRecordsFound';
import Popper from 'components/global/Popper';
import Skeleton from 'components/global/Skeleton';
import SubformHeader from 'components/global/SubformHeader';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useDebounce from 'hooks/useDebounce';
import useMediaQuery from 'hooks/useMediaQuery';

import ConnectSalesChannelModal from 'pages/SalesChannels/ConnectSalesChannelModal';
import DeleteStore from 'pages/SalesChannels/Stores/DeleteStore/DeleteStore';
import { setSalesChannel } from 'pages/SalesChannels/salesChannelDetailsSlice';

import { SALES_CHANNELS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import PATHS from 'utils/paths';

import { faPenToSquare, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { faEllipsis } from '@fortawesome/pro-solid-svg-icons';

import { getTokens, setConnectedTokens } from './ConnectedTokens.slice';
import * as S from './ConnectedTokens.styled';

const ConnectedTokens = () => {
	const { data: tokens, loading } = useSelector((state) => state?.connectedTokens);

	const dispatch = useDispatch();
	const [searchValue, setSearchValue] = useState(null);
	const debouncedValue = useDebounce(searchValue, 500);

	useEffect(() => {
		dispatch(getTokens());
	}, []);

	const filteredList = useMemo(() => {
		if (!debouncedValue) return tokens;

		return tokens?.filter((item) => {
			return item.tokenName && item.tokenName.toLowerCase().includes(debouncedValue.toLowerCase());
		});
	}, [debouncedValue, tokens]);

	const renderContent = () => {
		if (loading) {
			return (
				<S.PartnersContainer>
					{[...Array(5)].map((_, i) => (
						<S.LoadingRowSpaced key={i}>
							<S.LoadingRowCentered>
								<Skeleton width="7rem" height="7rem" radius={6} />
								<S.LoadingContent>
									<Skeleton width="12rem" height="2rem" />
									<S.LoadingMetaWrapper>
										<Skeleton width="7rem" height="1.6rem" />
										<Skeleton width="9rem" height="1.6rem" />
									</S.LoadingMetaWrapper>
								</S.LoadingContent>
							</S.LoadingRowCentered>

							<S.LoadingRowCenteredActions>
								<Skeleton width="4rem" height="2.4rem" radius={12} />
								<S.Separator />
								<Skeleton width="4rem" height="4rem" radius="50%" />
							</S.LoadingRowCenteredActions>
						</S.LoadingRowSpaced>
					))}
				</S.PartnersContainer>
			);
		}

		if (filteredList?.length) {
			return (
				<S.PartnersContainer className="listing-container">
					{filteredList?.map((data) => (
						<TokenBox key={data.ID} store={data} />
					))}
				</S.PartnersContainer>
			);
		} else {
			return (
				<S.PartnersContainer className="mt-5">
					<NoRecordsFound />
				</S.PartnersContainer>
			);
		}
	};

	return (
		<NonFormContainer>
			<SubformHeader
				boldTitle="connectedAPITokens"
				lightTitle="connectedAPITokensDescription"
				className="table-lord-icon"
				trigger="hover"
			/>

			<S.InputContainer>
				<SearchInput setValue={setSearchValue} value={searchValue} placeholder="searchAPIToken" fullWidth />
			</S.InputContainer>

			{renderContent()}
		</NonFormContainer>
	);
};

const TokenBox = ({ store }) => {
	// Hooks and states declaration
	const { ID, storeName, saleChannelID, integrationName, active, icon } = store;
	const { t } = useTranslation();
	const axiosPrivate = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const dispatch = useDispatch();
	const isMobile = useMediaQuery(Breakpoints.mobileLandscape);
	const navigate = useNavigate();

	const { data: tokens } = useSelector((state) => state?.connectedTokens);

	const [menuButtonRef, setMenuButtonRef] = useState(null);

	const statusChange = async () => {
		try {
			const response = await axiosPrivate.post(SALES_CHANNELS.UPDATE_SALE_CHANNEL_STATUS, {
				accountID: ID,
				status: !active
			});
			if (response.status === 200) {
				dispatch(
					setConnectedTokens(tokens.map((store) => (store.ID === ID ? { ...store, active: !active } : store)))
				);
				showSuccess();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			if (error.response?.data.code === 'error.F014') {
				navigate(PATHS.OTO_MODULES);
				showError({
					header: 'info.limitedShippingPartnersTitle',
					message: 'info.limitedShippingPartnersDescription'
				});
			} else {
				showError();
				AppLogger.error(error);
			}
		}
	};

	const openEditModal = (closePopper) => {
		dispatch(setSalesChannel(store));
		NiceModal.show(ConnectSalesChannelModal, {
			ID,
			salesChannelID: saleChannelID
		});
		closePopper();
	};

	const handleRemove = (closePopper) => {
		NiceModal.show(DeleteStore, { salesChannel: store });
		closePopper();
	};

	return (
		<S.RowSpaced>
			<S.RowCentered>
				<Image width="7rem" height="7rem" url={icon} objectFit="scale-down" radius={6} />
				<S.StoreInfo>
					<S.IntegrationName>
						<Typography noWrap={true} variant={isMobile ? 'p3' : 'p1'} fontWeight="bold">
							{t(`salesChannel.${integrationName}`)}
						</Typography>

						<Status
							value={active ? 'active' : 'inactive'}
							style={{
								height: 'unset',
								padding: '0 var(--padding-1)'
							}}
						/>
					</S.IntegrationName>
					<S.StoreName>
						<Typography color="var(--content-3)" noWrap>
							<Trans
								i18nKey="connectedStoreName"
								values={{
									storeName
								}}
								components={{
									storeName: <Typography fontWeight="500" noWrap style={{ display: 'inline' }} />
								}}
							/>
						</Typography>
					</S.StoreName>
				</S.StoreInfo>
			</S.RowCentered>

			<S.RowCenteredActions>
				<SwitchInput value={active} setValue={statusChange} />
				<S.Separator />
				<Button
					ref={setMenuButtonRef}
					icon={faEllipsis}
					variant="transparent"
					size="large"
					iconSize="medium"
					iconButton
					color="var(--black)"
				/>

				{menuButtonRef && (
					<Popper refElement={menuButtonRef} timeout={100}>
						{(closePopper) => (
							<S.Menu id="rowMenuContainer">
								{isMobile && (
									<Typography variant="p1" className="px-4" fontWeight="500">
										moreActions
									</Typography>
								)}
								<S.MenuItem
									onContextMenu={() => openEditModal(closePopper)}
									onClick={() => openEditModal(closePopper)}>
									<Icon icon={faPenToSquare} />
									<Typography>editCredentials</Typography>
								</S.MenuItem>
								<S.MenuItem
									onContextMenu={() => handleRemove(closePopper)}
									onClick={() => handleRemove(closePopper)}>
									<Icon icon={faTrashCan} color="var(--red)" />
									<Typography color="var(--red)">remove</Typography>
								</S.MenuItem>
							</S.Menu>
						)}
					</Popper>
				)}
			</S.RowCenteredActions>
		</S.RowSpaced>
	);
};

TokenBox.propTypes = {
	store: PropTypes.shape({
		ID: PropTypes.number.isRequired,
		storeName: PropTypes.string.isRequired,
		saleChannelID: PropTypes.number.isRequired,
		integrationName: PropTypes.string.isRequired,
		active: PropTypes.bool.isRequired,
		icon: PropTypes.string,
		statusMappingUpdateTypes: PropTypes.string,
		statusMapping: PropTypes.bool,
		storeLocation: PropTypes.bool,
		syncOrders: PropTypes.bool,
		syncProducts: PropTypes.bool
	}).isRequired
};

export default ConnectedTokens;
